import type { ProbeResult as Result, Step, StepId } from "@micropod/domain";
import { Pane, PaneHeader } from "@micropod/ui";
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Icon } from "../ui/Icon";
import { ProbeResult } from "./ProbeResult";

type Results = Partial<Record<StepId, Result>>;

interface WizardState {
  steps: readonly Step[];
  completed: StepId[];
  connector: {
    id: string;
    callbackUrl: string;
    verifyToken: string | null;
    phoneNumberId: string | null;
    wabaId: string | null;
  } | null;
}

export function Wizard() {
  const [state, setState] = useState<WizardState | null>(null);
  const [active, setActive] = useState<StepId>("create_app");
  const [results, setResults] = useState<Results>({});
  const [busy, setBusy] = useState<StepId | null>(null);
  const [ids, setIds] = useState({ phoneNumberId: "", wabaId: "" });
  const [secrets, setSecrets] = useState({ accessToken: "", appSecret: "" });

  const load = useCallback(async () => {
    const s = (await api.onboarding.state.query()) as unknown as WizardState;
    setState(s);
    const next = s.steps.find((x) => !s.completed.includes(x.id));
    if (next) setActive(next.id);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runProbe(step: StepId) {
    if (!state?.connector) return;
    setBusy(step);
    try {
      const r = (await api.onboarding.probe.mutate({
        connectorId: state.connector.id,
        step,
      })) as unknown as Result;
      setResults((prev) => ({ ...prev, [step]: r }));
      if (r.ok) await load();
    } finally {
      setBusy(null);
    }
  }

  async function begin() {
    setBusy("connect_number");
    try {
      await api.onboarding.begin.mutate(ids);
      await load();
      setActive("access_token");
    } finally {
      setBusy(null);
    }
  }

  async function storeSecrets(step: StepId) {
    if (!state?.connector) return;
    setBusy(step);
    try {
      await api.onboarding.saveSecrets.mutate({
        connectorId: state.connector.id,
        ...(secrets.accessToken ? { accessToken: secrets.accessToken } : {}),
        ...(secrets.appSecret ? { appSecret: secrets.appSecret } : {}),
      });
    } finally {
      setBusy(null);
    }
    await runProbe(step);
  }

  if (!state) {
    return (
      <Pane kind="thread">
        <PaneHeader>Connect WhatsApp</PaneHeader>
        <div className="scroll pane-body">
          <div className="skeleton" />
          <div className="skeleton skeleton-short" />
        </div>
      </Pane>
    );
  }

  const step = state.steps.find((s) => s.id === active);
  const done = state.completed.length;
  const total = state.steps.length;

  return (
    <>
      {/* Left pane: where they are. Divided by a hairline, not floating in a card. */}
      <Pane as="nav" kind="steps" label="Setup steps">
        <PaneHeader>
          Connect WhatsApp
          <span className="counter tabular">
            {done}/{total}
          </span>
        </PaneHeader>
        <ol className="scroll steplist">
          {state.steps.map((s, i) => {
            const isDone = state.completed.includes(s.id);
            const isActive = s.id === active;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`steprow${isActive ? " on" : ""}${isDone ? " done" : ""}`}
                  onClick={() => setActive(s.id)}
                  aria-current={isActive ? "step" : undefined}
                >
                  <span className="stepnum tabular" aria-hidden="true">
                    {isDone ? <Icon name="check" size={11} /> : i + 1}
                  </span>
                  <span className="steptitle">{s.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </Pane>

      {/* Right pane: the one step in hand. */}
      <Pane kind="thread">
        <PaneHeader>{step?.title}</PaneHeader>
        <div className="scroll pane-body">
          {step && (
            <>
              <p className="instruction">{step.instruction}</p>

              {/* Stated BEFORE they act, so the probe is a promise rather than a surprise. */}
              <p className="proves">
                <span className="proves-label">This will prove</span>
                {step.proves}
              </p>

              {step.id === "connect_number" && !state.connector && (
                <div className="fields">
                  <label className="field">
                    <span>Phone number ID</span>
                    <input
                      value={ids.phoneNumberId}
                      onChange={(e) => setIds({ ...ids, phoneNumberId: e.target.value })}
                      placeholder="e.g. 109876543210987"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                    <small>
                      A long number from your app's WhatsApp settings — not your phone number.
                    </small>
                  </label>
                  <label className="field">
                    <span>WhatsApp Business Account ID</span>
                    <input
                      value={ids.wabaId}
                      onChange={(e) => setIds({ ...ids, wabaId: e.target.value })}
                      placeholder="e.g. 102938475610293"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={begin}
                    disabled={!ids.phoneNumberId || !ids.wabaId || busy !== null}
                  >
                    {busy ? "Checking…" : "Continue"}
                  </button>
                </div>
              )}

              {step.id === "access_token" && state.connector && (
                <div className="fields">
                  <label className="field">
                    <span>System User access token</span>
                    <input
                      type="password"
                      value={secrets.accessToken}
                      onChange={(e) => setSecrets({ ...secrets, accessToken: e.target.value })}
                      placeholder="EAAG…"
                      autoComplete="off"
                    />
                    <small>
                      Encrypted the moment it arrives. It is never shown again, not even to you.
                    </small>
                  </label>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => storeSecrets("access_token")}
                    disabled={!secrets.accessToken || busy !== null}
                  >
                    {busy === "access_token" ? "Asking Meta…" : "Check the token"}
                  </button>
                </div>
              )}

              {step.id === "app_secret" && state.connector && (
                <div className="fields">
                  <label className="field">
                    <span>App secret</span>
                    <input
                      type="password"
                      value={secrets.appSecret}
                      onChange={(e) => setSecrets({ ...secrets, appSecret: e.target.value })}
                      placeholder="From Basic Settings in your Meta app"
                      autoComplete="off"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => storeSecrets("app_secret")}
                    disabled={!secrets.appSecret || busy !== null}
                  >
                    {busy === "app_secret" ? "Asking Meta…" : "Check permissions"}
                  </button>
                </div>
              )}

              {step.id === "webhook" && state.connector && (
                <div className="fields">
                  <Copyable label="Callback URL" value={state.connector.callbackUrl} />
                  <Copyable label="Verify token" value={state.connector.verifyToken ?? ""} />
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => runProbe("webhook")}
                    disabled={busy !== null}
                  >
                    {busy === "webhook" ? "Checking…" : "Check the address"}
                  </button>
                </div>
              )}

              {step.id === "test_message" && state.connector && (
                <div className="fields">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => runProbe("test_message")}
                    disabled={busy !== null}
                  >
                    {busy === "test_message" ? "Looking…" : "I have sent it"}
                  </button>
                </div>
              )}

              {results[step.id] && <ProbeResult result={results[step.id] as Result} />}
            </>
          )}
        </div>
      </Pane>
    </>
  );
}

/** A value we generate and they paste into Meta. Copying it must be one action. */
function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <span>{label}</span>
      <div className="copyrow">
        <code>{value}</code>
        <button
          type="button"
          className="btn"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
